import { createContext, useContext, useEffect, useState } from "react";
import React from "react";
import { applyTheme } from "../utils";
import { themes, AvailableThemes } from "../themes-data";

type Theme = "dark" | "light" | "system";

type BrandTheme = keyof typeof AvailableThemes;

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultBrand?: BrandTheme;
  storageKey?: string;
  externalTheme?: Theme;
  externalBrand?: BrandTheme;
};

type ThemeProviderState = {
  theme: Theme;
  brand: BrandTheme;
  setTheme: (theme: Theme) => void;
  setBrand: (brand: BrandTheme) => void;
};

const initialState: ThemeProviderState = {
  theme: "light",
  brand: "base",
  setTheme: () => null,
  setBrand: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "light",
  defaultBrand = AvailableThemes.base,
  storageKey = "ui-theme",
  externalTheme,
  externalBrand,
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [brand, setBrand] = useState<BrandTheme>(defaultBrand);

  // Handle theme changes from externalTheme
  useEffect(() => {
    if (externalTheme && externalTheme !== theme) {
      setTheme(externalTheme);
    }
  }, [externalTheme, theme]);

  // Handle brand changes from externalBrand
  useEffect(() => {
    if (externalBrand && externalBrand !== brand) {
      setBrand(externalBrand);
      const themeToApply = themes[externalBrand] ? externalBrand : AvailableThemes.base;
      applyTheme(themeToApply, 'v2');
    }
  }, [externalBrand, brand]);

  // Apply theme changes (light/dark/system)
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  // Apply brand changes
  useEffect(() => {
    const root = window.document.documentElement;
    root.setAttribute("data-brand", brand);
    const themeToApply = themes[brand] ? brand : AvailableThemes.base;
    applyTheme(themeToApply, 'v2');
  }, [brand]);

  const value = {
    theme,
    brand,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme);
      setTheme(newTheme);
    },
    setBrand: (newBrand: BrandTheme) => {
      localStorage.setItem("ui-brand", newBrand);
      setBrand(newBrand);
    },
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
